import { useWorkspaceTab } from '@components/Common/EntityWorkspace/useWorkspaceTab';
import ConfirmDialog from '@components/Common/ConfirmDialog';
import CRMTabs from '@components/Common/CRMTabs';
import SkeletonLoader from '@components/Common/Skeleton/SkeletonLoader';
import { useActivityRail } from '@components/Common/EntityWorkspace/useActivityRail';
import CRMStatusChip from '@components/Common/CRMStatusChip';
import {
  ActivityRail,
  EntityWorkspace,
  IdentityRail,
  SectionCard,
  WorkspaceField,
  WorkspaceFieldLabel,
} from '@components/Common/EntityWorkspace';
import RecordActivityCard from '@components/Common/EntityWorkspace/RecordActivityCard';
import type { GlanceRow, TimelineEntry, WorkspaceTab } from '@components/Common/EntityWorkspace';
import type { JobFormData } from '@components/Jobs/types';
import ErrorMessage from '@components/Common/ErrorMessage';
import Loader from '@components/Common/Loader';
import MintlyAIBadge from '@components/Common/MintlyAIBadge';
import RecordPickerDialog from '@components/Common/RecordPickerDialog';
import type { PickerRow } from '@components/Common/RecordPickerDialog';
import SelectFormField from '@components/Common/SelectFormField';
import { TextEditor } from '@components/Common/TextEditor/TextEditor';
import { useToast } from '@components/Common/Toast/ToastProvider';
import RelevancyCandidatesTable from '@components/Jobs/matching/RelevancyCandidatesTable';
import SuggestedCandidatesTab from '@components/Jobs/matching/SuggestedCandidatesTab';
import {
  HIRING_MODE_OPTIONS,
  MANAGED_WITHOUT_RECRUITER_WARNING,
} from '@components/Common/Utils/hiringMode';
import { canAssignJobs, useCanWriteJobsAndCompanies, useCurrentUserRole } from '@components/Common/Utils/roles';
import { toHrUsers } from '@components/Common/Utils/useHrUsers';
import './index.scss';
import {
  assignJobHrMutation,
  setJobHiringModeMutation,
  createJobApplicationMutation,
  requestRightToRepresentMutation,
  createJobEmbedding,
  getJobDataById,
  jobApplicantsList,
  jobSeeker as jobSeekerQuery,
  updateJobDataById,
  generateJobSeoDescription as generateJobSeoDescriptionMutation,
  getActiveValuesByTypeQuery,
} from '@constants/graphQLQueries';
import { FALLBACK_CATEGORIES, FALLBACK_INDUSTRIES } from '@constants/jobMetadataConstants';
import { EXPERIENCE_LEVELS, JOB_TYPES, experienceLevelForYears, jobTypeLabel, toJobStatusEnum, toJobTypeEnum } from '@constants/jobEnums';
import { SALARY_CURRENCIES, SALARY_FREQUENCY_OPTIONS, salaryFrequencyLabel } from '@constants/salary';
import { orFilter } from '@components/Common/CommandPalette/paletteSearch';
import WorkspaceInput from '@components/Common/WorkspaceInput';
import PipelineTab from './PipelineTab';
import { REQUIREMENT_STATUSES, requirementOf } from '@constants/applicationStatus';
import { currentUserDetails } from '@store/accessor';
import { fetchGraphQLApiData } from '@helpers/graphQLApi';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import SupervisorAccountOutlinedIcon from '@mui/icons-material/SupervisorAccountOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import HistoryIcon from '@mui/icons-material/History';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import SellOutlinedIcon from '@mui/icons-material/SellOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import WorkOutlineIcon from '@mui/icons-material/WorkOutlineOutlined';
import { Autocomplete, Box, Button, Chip, FormControlLabel, IconButton, Switch, TextField, Typography } from '@mui/material';
import axios from 'axios';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { jobApplicantsPath } from '@components/Common/Utils/filterLinks';
import { formatDate } from '@helpers/date';
import { sanitizeHtml } from '@helpers/utils';

const TABS: WorkspaceTab[] = [
  { id: 'details', label: 'Details', icon: <WorkOutlineIcon /> },
  { id: 'candidates', label: 'Candidates', icon: <AutoAwesomeOutlinedIcon /> },
  { id: 'pipeline', label: 'Pipeline', icon: <GroupsOutlinedIcon /> },
  { id: 'seo', label: 'SEO', icon: <SearchOutlinedIcon /> },
  { id: 'history', label: 'History', icon: <HistoryIcon /> },
];

/*
 * Job type, status and currency now come from `@constants/jobEnums` and `@constants/salary`.
 *
 * They used to be declared here as human strings -- 'Full Time', 'On hold', 'Open', 'Filled' --
 * and interpolated into the mutation as GraphQL enum literals. `jobType: Full Time` is a parse
 * error and `Open`/`Filled` are not members of {OPEN, CLOSED, EXPIRED}, so touching either
 * dropdown made the whole save fail; and a job stored as `FULL_TIME` matched no option, so the
 * select rendered blank. 'On hold' and 'Filled' are gone because the schema has no equivalent
 * and they could never have been stored. See the note in constants/jobEnums.ts.
 */

/**
 * The form fields each card owns, so saving a card sends only that card's fields.
 *
 * The mutation builder already omits absent keys, which is what stops a save writing `""` over
 * data the workspace never loaded. This is the second half: it stops a save writing a value the
 * user did not touch. `toForm` coerces every null to `''`, so without this, opening the Location
 * card and pressing Save would write `city: ""` onto a job whose city was simply unset.
 *
 * `salesOwnerId` is deliberately in no section: it is loaded but never edited or displayed, so
 * it must never be sent. It was one of the fields the old whole-form save blanked.
 */
const SECTION_FIELDS: Record<string, (keyof Form)[]> = {
  role: ['title', 'companyName', 'requirementStatus', 'requirementReason', 'jobType', 'isRemote', 'numberOfPositions', 'category', 'industry'],
  ident: ['jobCode', 'jobSource', 'jobUrl', 'expiryDate'],
  loc: ['location', 'city', 'state', 'country', 'zipCode'],
  comp: ['salary', 'salaryCurrency', 'salaryMin', 'salaryMax', 'salaryFrequency', 'experience', 'yearsOfExperience', 'education'],
  desc: ['description'],
  reqs: ['requirements'],
  resp: ['responsibilities'],
  skills: ['skillsRequired', 'tags'],
  ops: ['salesOwnerName', 'emailAddress', 'phoneNumber', 'internalFilters', 'dataAvailable'],
  vis: ['isFeatured', 'isVerified', 'seoPosting'],
  // Handled separately: the four meta* keys are assembled into the nested `metadata` input.
  seo: [],
};

const initialsOf = (name?: string) =>
  (name || '').split(' ').filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase()).join('') || 'JB';
const fmt = (d?: string) => formatDate(d);

/** Editable shape. Mirrors the fields updateJobDataById accepts. */
interface Form {
  id: string;
  jobCode: string; jobUrl: string; jobSource: string;
  title: string; companyId: string | null; companyName: string;
  description: string; jobType: string; jobStatus: string;
  requirementStatus: string; requirementReason: string;
  industry: string; category: string;
  location: string; city: string; state: string; country: string; zipCode: string;
  salary: string; salaryCurrency: string; salaryMin: number | null; salaryMax: number | null;
  salaryFrequency: string;
  expiryDate: string; emailAddress: string; phoneNumber: string; summary: string;
  isFeatured: boolean; isVerified: boolean; seoPosting: boolean;
  experience: string; yearsOfExperience: number; education: string;
  skillsRequired: string[]; requirements: string; responsibilities: string;
  tags: string[]; isRemote: boolean;
  isExternalJob: boolean;
  /** MANAGED roles are the ones a recruiter is assigned to work. */
  hiringMode: string;
  /** The dedicated HR working this role, or null. One per job. */
  assignedHrId: string | null;
  salesOwnerName: string; salesOwnerId: string; numberOfPositions: number | null;
  internalFilters: { key: string; value: string }[];
  dataAvailable?: boolean | null;
  metaTitle: string; metaUrl: string; metaDescription: string; metaKeywords: string;
}

const toForm = (j: any): Form => ({
  id: j.id || j._id || '',
  jobCode: j.jobCode ?? '', jobUrl: j.jobUrl ?? '', jobSource: j.jobSource ?? '',
  title: j.title ?? '',
  companyId: j.companyId ?? j.company?._id ?? null,
  companyName: j.companyName ?? j.company?.companyName ?? '',
  description: j.description ?? '',
  /*
   * Normalised on the way IN as well as out. A job stored as `FULL_TIME` has to match an option
   * whose value is `FULL_TIME`, and a legacy row holding `'Full Time'` has to resolve to the same
   * thing -- otherwise the select renders blank and the admin cannot tell a missing value from an
   * unmatched one. An unrecognised value becomes '' so the control shows its placeholder rather
   * than silently re-sending something the schema will reject.
   */
  jobType: toJobTypeEnum(j.jobType) ?? '', jobStatus: toJobStatusEnum(j.jobStatus) ?? '',
  // A job from before requirement status reads its listing flag (OPEN -> Active, else Closed).
  requirementStatus: requirementOf(j.requirementStatus, j.jobStatus),
  // Blank on load: a reason belongs to the next change, not the last one.
  requirementReason: '',
  industry: j.industry ?? '', category: j.category ?? '',
  location: j.location ?? '', city: j.city ?? '', state: j.state ?? '',
  country: j.country ?? '', zipCode: j.zipCode ?? '',
  /*
   * No default currency. This was `?? 'INR'`, which did two wrong things: `??` does not catch
   * `''`, so a record already damaged to an empty string still showed a blank box; and on a
   * record that genuinely had no currency it stamped INR onto the next save, inventing a fact
   * about someone's salary. The select shows its placeholder instead.
   */
  salary: j.salary ?? '', salaryCurrency: j.salaryCurrency ?? '',
  salaryMin: j.salaryMin ?? null, salaryMax: j.salaryMax ?? null,
  salaryFrequency: j.salaryFrequency ?? '',
  expiryDate: j.expiryDate ? String(j.expiryDate).slice(0, 10) : '',
  emailAddress: j.emailAddress ?? '', phoneNumber: j.phoneNumber ?? '',
  summary: j.summary ?? '',
  hiringMode: j.hiringMode ?? '', assignedHrId: j.assignedHrId ?? null,
  isFeatured: !!j.isFeatured, isVerified: !!j.isVerified, seoPosting: !!j.seoPosting,
  experience: j.experience ?? '', yearsOfExperience: j.yearsOfExperience ?? 0,
  education: j.education ?? '',
  skillsRequired: j.skillsRequired ?? [],
  requirements: j.requirements ?? '', responsibilities: j.responsibilities ?? '',
  tags: j.tags ?? [], isRemote: !!j.isRemote,
  isExternalJob: !!j.isExternalJob,
  salesOwnerName: j.salesOwnerName ?? '', salesOwnerId: j.salesOwnerId ?? '',
  numberOfPositions: j.numberOfPositions ?? null,
  internalFilters: j.internalFilters ?? [],
  dataAvailable: j.dataAvailable ?? null,
  metaTitle: j.metadata?.title ?? '', metaUrl: j.metadata?.url ?? '',
  metaDescription: j.metadata?.description ?? '', metaKeywords: j.metadata?.keywords ?? '',
});

/**
 * What a requirement-status change will do, said before it is saved. New and Active are on the
 * job listings; On Hold, Filled and Closed are not, and lock the pipeline (a candidate can still
 * be closed). Filled and Closed are final unless a Super Admin reopens with a reason.
 */
const requirementHint = (from: string, to: string): string | undefined => {
  if (from === to) return undefined;
  if ((from === 'Filled' || from === 'Closed') && to !== from) {
    return 'Reopening is for a Super Admin, and needs a reason.';
  }
  if (to === 'On Hold') return 'Comes off the job listings. Candidates who applied are told hiring is paused.';
  if (to === 'Filled' || to === 'Closed') {
    return 'Comes off the job listings, and candidates can only be closed from here on.';
  }
  return 'Shown on the job listings.';
};

const JobWorkspace: React.FC = () => {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [job, setJob] = useState<any | null>(null);
  const [form, setForm] = useState<Form | null>(null);
  const [loading, setLoading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /* In the URL, not in state: a tab is a place, and this one was unshareable and did
     not survive a reload. See useWorkspaceTab. */
  const [activeTab, setActiveTab] = useWorkspaceTab(TABS, 'details');
  const [activityOpen, toggleActivity] = useActivityRail();

  const [editing, setEditing] = useState<string | null>(null);
  /*
   * Only a Super Admin or an HR Lead may set the hiring mode or the recruiter. Both decide who can
   * see a job's candidates: a MANAGED job's applicants are the private book of its assignedHrId,
   * so an HR who could set these could grant themselves an account or take one from a colleague.
   * Handing jobs to recruiters is the HR Lead's work (owner, 2026-11-03).
   *
   * assignJobHr is gated the same way on the BFF, so that half is enforced rather than
   * merely hidden. updateJobById is not on that list -- it is the ordinary job-edit mutation an
   * HR legitimately uses -- so the hiring-mode control is gated here only. Worth knowing before
   * treating this as a security boundary; see the note in the Managed hiring card.
   */
  const canManageHiring = canAssignJobs(useCurrentUserRole());
  // Marketing reads every job but edits none (owner's brief, 2026-10-19). The BFF and backend refuse the writes too.
  const canWriteJobs = useCanWriteJobsAndCompanies();
  const [dirty, setDirty] = useState(false);
  // Names for `assignedHrId`. Only fetched for MANAGED roles — a self-serve job
  // is never assigned to a recruiter, so the request would be dead weight on
  // the overwhelming majority of job views.
  const [hrUsers, setHrUsers] = useState<Array<{ _id: string; name: string }>>([]);
  const [skillInput, setSkillInput] = useState('');
  const [tagInput, setTagInput] = useState('');

  // ── AI candidate panels ──
  const [aiTab, setAiTab] = useState(0);
  const [addApplicantOpen, setAddApplicantOpen] = useState(false);
  /*
   * Two ways onto a job. "Add applicant" asks for consent at once (created at Qualified), which is
   * how a recruiter submits someone already agreed; the Pipeline tab's "Add candidate" starts them
   * at Sourcing, the top of the funnel, with nothing sent to anyone.
   */
  const [addMode, setAddMode] = useState<'consent' | 'sourcing'>('consent');
  // Bumped after an add so the Pipeline tab reloads its rows.
  const [pipelineKey, setPipelineKey] = useState(0);
  const [submittingApplicant, setSubmittingApplicant] = useState(false);
  const [relevant, setRelevant] = useState<any[]>([]);
  const [relevantLoading, setRelevantLoading] = useState(false);
  const [relevantPage, setRelevantPage] = useState(0);
  const [nearby, setNearby] = useState<any[]>([]);
  const [nearbyLoading, setNearbyLoading] = useState(false);
  const [nearbyPage, setNearbyPage] = useState(0);
  const PAGE = 10;

  const load = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await fetchGraphQLApiData(getJobDataById(id));
      const record = data?.getJobById ?? data?.getJobDataById;
      if (!record) {
        setError('Job not found.');
        return;
      }
      setJob(record);
      setForm(toForm(record));
    } catch {
      setError('Failed to load the job. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (jobId) load(jobId);
  }, [jobId, load]);

  /*
   * The internal recruiter directory: names for `assignedHrId`, and the options for the picker.
   *
   * Fetched for a MANAGED job (to show who is working it) and for any job a Super Admin is
   * looking at (they can turn it MANAGED, and an empty dropdown at that moment reads as "there
   * are no recruiters"). Still skipped for the common case -- an HR viewing a self-serve job.
   *
   * The rows are mapped to {_id, name} here rather than stored raw. They were stored raw, which
   * matched neither the state's type nor the lookup that reads `.name`, so every assigned
   * recruiter rendered as a 24-character id -- the one thing the lookup existed to avoid.
   * Filtered to role HR for the same reason JobAssignmentList does: assigning a job to a Super
   * Admin or a Marketing user is not a thing.
   */
  useEffect(() => {
    if (form?.hiringMode !== 'MANAGED' && !canManageHiring) return;
    let cancelled = false;
    axios
      .get('/api/users?page=0&size=200')
      .then((r) => {
        if (cancelled) return;
        setHrUsers(toHrUsers(r?.data?.content ?? r?.data ?? []));
      })
      .catch(() => {
        /* name lookup is cosmetic; the id still renders */
      });
    return () => {
      cancelled = true;
    };
  }, [form?.hiringMode, canManageHiring]);

  const fetchRelevant = useCallback(async (id: string) => {
    setRelevantLoading(true);
    try {
      const { data } = await axios.get('/api/recommend/realtime/candidates-for-job', {
        params: { jobId: id, limit: 200 },
      });
      setRelevant(data || []);
      setRelevantPage(0);
    } catch {
      setRelevant([]);
    } finally {
      setRelevantLoading(false);
    }
  }, []);

  const fetchNearby = useCallback(async (id: string) => {
    setNearbyLoading(true);
    try {
      const { data } = await axios.get('/api/recommend/nearby/candidates-for-job', {
        params: { jobId: id, limit: 50 },
      });
      setNearby(data || []);
      setNearbyPage(0);
    } catch {
      setNearby([]);
    } finally {
      setNearbyLoading(false);
    }
  }, []);

  // External jobs are mirrored from a source; we neither score nor match them.
  useEffect(() => {
    if (!form?.id || form.isExternalJob) return;
    fetchRelevant(form.id);
    fetchNearby(form.id);
  }, [form?.id, form?.isExternalJob, fetchRelevant, fetchNearby]);

  /**
   * Build the embedding in the background when a job has none. Matching is
   * useless without it, and making the operator trigger it by hand would be
   * asking them to do the system's bookkeeping.
   */
  useEffect(() => {
    if (!job || job.isExternalJob) return;
    const hasV2 = Array.isArray(job.embeddingsV2) && job.embeddingsV2.length > 0;
    const hasV1 = Array.isArray(job.embeddings) && job.embeddings.length > 0;
    if (hasV1 || hasV2) return;
    (async () => {
      try {
        const id = job.id || job._id;
        const { data } = await fetchGraphQLApiData(createJobEmbedding(id));
        if (data?.createJobEmbedding?._id) {
          await load(id);
          fetchRelevant(id);
        }
      } catch {
        // Matching degrades to empty; the record itself is unaffected.
      }
    })();
  }, [job, load, fetchRelevant]);

  const setField = <K extends keyof Form>(key: K, value: Form[K]) => {
    setForm((p) => (p ? { ...p, [key]: value } : p));
    setDirty(true);
  };

  const startEdit = (id: string) => { setEditing(id); setDirty(false); };
  const cancelEdit = () => {
    if (job) setForm(toForm(job));
    setEditing(null);
    setDirty(false);
  };

  /**
   * Save one card.
   *
   * Sends the id plus ONLY the fields that card owns. Everything else is absent from the mutation
   * document, and the backend's per-field `Optional.ofNullable(...).ifPresent(...)` merge leaves
   * an absent field exactly as it was.
   *
   * This used to spread the whole form through an `as any` cast, which is how saving the
   * Compensation card came to write `employerId: "undefined"` and blank `ageLimit`, `gender`,
   * `postType`, `sponsored`, `locality` and `placeId`, and wipe the agentic `mustHaveSkills` and
   * `niceToHaveSkills` lists -- none of which this screen even renders.
   */
  const saveSection = async (sectionId: string) => {
    if (!form || !jobId) return;
    if (!form.title.trim()) {
      showToast('Job title is required', 'error');
      return;
    }

    const payload: Partial<JobFormData> & { id: string } = { id: form.id || jobId };
    for (const key of SECTION_FIELDS[sectionId] ?? []) {
      (payload as Record<string, unknown>)[key] = form[key];
    }
    /*
     * The SEO card edits four flat fields that the schema takes as one nested object, and
     * `metadata` replaces wholesale rather than merging -- so the existing `metadata.metadata`
     * rows have to be carried through or they are lost.
     */
    if (sectionId === 'seo') {
      payload.metadata = {
        title: form.metaTitle,
        url: form.metaUrl,
        description: form.metaDescription,
        keywords: form.metaKeywords,
        metadata: job?.metadata?.metadata ?? [],
      };
    }

    try {
      const res = await fetchGraphQLApiData(updateJobDataById(payload), {
        showStatus: true,
        message: 'Saving job',
      });
      /*
       * A GraphQL response can carry `errors` AND a non-null `data`, and fetchGraphQLApiData
       * only throws when `data` is null -- so a partially applied write returned here as a
       * success and the user was told the job saved. Every failure described above was invisible
       * for exactly this reason.
       */
      if (res?.errors?.length) {
        throw new Error(res.errors.map((e: { message: string }) => e.message).join('; '));
      }
      setEditing(null);
      setDirty(false);
      await load(jobId);
    } catch (e: any) {
      // The requirement-status refusals ("Only a Super Admin can reopen it.") are written for
      // this screen, so they are shown; anything else keeps the generic line.
      const msg = String(e?.message ?? '').replace(/^GraphQL errors:\s*/, '');
      showToast(/reopen|Super Admin|reason/i.test(msg) ? msg : 'Failed to save the job', 'error');
    }
  };

  /**
   * Save the Managed hiring card.
   *
   * Two mutations because the two fields have different gates: the mode rides `updateJobById`
   * (the ordinary job update), the recruiter rides `assignJobHr`, which the BFF restricts to
   * Super Admins. Only what actually changed is sent, so re-saving the card without touching
   * anything is a no-op rather than two writes and an audit entry.
   *
   * The recruiter is cleared when the job goes back to self-serve. Leaving a stale assignedHrId
   * is survivable -- every gate keys off hiringMode, not off the presence of an id -- but it
   * would show a recruiter on a job nobody is working, and the next admin would have no way to
   * tell whether that meant anything.
   */
  const saveManagedHiring = async () => {
    if (!form || !jobId) return;
    const nextMode = form.hiringMode || 'SELF_SERVE';
    const nextHr = nextMode === 'MANAGED' ? form.assignedHrId || null : null;
    const modeChanged = nextMode !== (job?.hiringMode || 'SELF_SERVE');
    const hrChanged = nextHr !== (job?.assignedHrId ?? null);
    if (!modeChanged && !hrChanged) {
      setEditing(null);
      setDirty(false);
      return;
    }
    try {
      if (modeChanged) {
        await fetchGraphQLApiData(setJobHiringModeMutation(jobId, nextMode), {
          showStatus: true,
          message: 'Updating hiring mode',
        });
      }
      if (hrChanged) {
        await fetchGraphQLApiData(assignJobHrMutation(jobId, nextHr), {
          showStatus: true,
          message: 'Updating recruiter',
        });
      }
      setEditing(null);
      setDirty(false);
      await load(jobId);
    } catch {
      showToast('Failed to update Premium Hiring', 'error');
    }
  };

  const handleDeleteJob = async () => {
    if (!jobId) return;
    setConfirmDelete(false);
    try {
      await fetchGraphQLApiData(`mutation { deleteJobById(_id: "${jobId}") { _id } }`, {
        showStatus: true,
        message: 'Deleting job',
      });
      showToast('Job deleted', 'success');
      navigate('/jobs');
    } catch {
      showToast('Failed to delete the job', 'error');
    }
  };

  const timeline: TimelineEntry[] = useMemo(() => {
    if (!job) return [];
    const t: TimelineEntry[] = [];
    if (job.updatedAt) t.push({ id: 'u', kind: 'status', text: 'Job updated', meta: `${fmt(job.updatedAt)} · ${job.updatedBy || 'System'}` });
    if (job.createdAt) t.push({ id: 'c', kind: 'submitted', text: 'Job created', meta: `${fmt(job.createdAt)} · ${job.createdBy || 'System'}` });
    return t;
  }, [job]);

  /*
   * Candidate search for the Add-applicant picker.
   *
   * Server-side, deliberately. The job picker on the candidate side fetches the 100 newest jobs
   * once and filters them in memory, so the 101st job is unreachable no matter what is typed; the
   * job-seeker table is far larger and that approach would be worse here. `jobSeeker` already
   * paginates server-side and `orFilter` already expresses name-or-email search — both reused
   * rather than re-derived.
   *
   * Already-applied candidates are shown but not selectable: the point of this dialog is to know
   * who is on the role, and hiding them would make an HR wonder whether the search was broken.
   */
  const searchCandidates = useCallback(
    async (query: string): Promise<PickerRow[]> => {
      const q = query.trim();
      const filters = q
        ? orFilter(['personalDetails.fullName', 'personalDetails.email'], q)
        : [];
      const [seekersRes, appliedRes] = await Promise.all([
        fetchGraphQLApiData(jobSeekerQuery(0, 20, filters)),
        fetchGraphQLApiData(
          jobApplicantsList(0, 500, [`{ key: "jobId", value: "${jobId}" }`]),
        ).catch(() => null),
      ]);

      const applied = new Set<string>(
        (appliedRes?.data?.getAllJobApplications?.content ?? [])
          .map((a: any) => a?.jobSeeker?._id)
          .filter(Boolean),
      );

      return (seekersRes?.data?.getAllJobSeekers?.content ?? []).map((c: any) => ({
        id: c._id,
        title: c.personalDetails?.fullName || c.personalDetails?.email || 'Unnamed',
        subtitle: [c.personalDetails?.email, c.personalDetails?.city]
          .filter(Boolean)
          .join(' · '),
        disabledReason: applied.has(c._id) ? 'Already applied' : undefined,
      }));
    },
    [jobId],
  );

  /*
   * Above the early returns below, deliberately. Placed after them this is a conditional hook: the
   * first render bails out at `loading` before reaching it and the next does not, which React
   * reports as "Rendered more hooks than during the previous render" and replaces the whole
   * workspace with the error boundary. The e2e smoke matrix caught exactly that.
   */

  /**
   * Ask the backend for a description and put it in the field for review.
   *
   * Nothing is saved here: the operator still presses Save on the section, which is what stops a
   * stray click replacing copy somebody wrote by hand. A null answer means generation is switched
   * off or the model's text failed the backend's length and style checks.
   */
  const [generatingSeo, setGeneratingSeo] = React.useState(false);
  const generateSeoDescription = async () => {
    if (!jobId) return;
    setGeneratingSeo(true);
    try {
      const res = await fetchGraphQLApiData(generateJobSeoDescriptionMutation(jobId));
      const suggestion = res?.data?.generateJobSeoDescription;
      if (suggestion) setField('metaDescription', suggestion);
    } finally {
      setGeneratingSeo(false);
    }
  };

  /*
   * Category and Industry, from the same list the create form offers.
   *
   * The two forms were inverted: create backs both with the live CATEGORY and INDUSTRY metadata
   * lists and requires them; this one was a bare text box with no suggestions at all, so an edit
   * could write a category the taxonomy has never heard of, and the filters that read that taxonomy
   * would then match nothing. The public /jobs/category/<name> pages are built from it.
   *
   * Free text still saves, exactly as it does on create: 4,000 existing jobs carry values that
   * predate the list, and a closed list would refuse to save a card that merely contains one.
   * Offering the list is the fix; forbidding everything else would be a different and larger one.
   */
  const [categoryOptions, setCategoryOptions] = useState<string[]>(FALLBACK_CATEGORIES);
  const [industryOptions, setIndustryOptions] = useState<string[]>(FALLBACK_INDUSTRIES);
  useEffect(() => {
    (async () => {
      try {
        const [cats, inds] = await Promise.all([
          fetchGraphQLApiData(getActiveValuesByTypeQuery('CATEGORY')),
          fetchGraphQLApiData(getActiveValuesByTypeQuery('INDUSTRY')),
        ]);
        const c: string[] = cats?.data?.getActiveValuesByType ?? [];
        const i: string[] = inds?.data?.getActiveValuesByType ?? [];
        if (c.length > 0) setCategoryOptions(c);
        if (i.length > 0) setIndustryOptions(i);
      } catch {
        // The fallback lists are the point of having them.
      }
    })();
  }, []);

  if (loading && !job) return <SkeletonLoader variant="workspace" />;
  if (error && !job) return <ErrorMessage page message={error} />;
  if (!job || !form) return null;

  const isEditing = (id: string) => editing === id;
  /** External jobs mirror a source, so editing them here would drift from it. */
  const canEdit = !form.isExternalJob && canWriteJobs;
  /*
   * A MANAGED job is worked by a Mintly recruiter for a client, which is the only situation where
   * an account owner and an internal screening brief mean anything. 127 of 5,328 jobs on
   * production are MANAGED, and both fields were empty on every single job, MANAGED included.
   */
  const isManagedJob = form.hiringMode === 'MANAGED';
  /** An inline editor that suggests, for a field whose values come from a controlled list. */
  const suggestInput = (key: keyof Form, options: string[]) => (
    <Autocomplete
      freeSolo
      size="small"
      options={options}
      value={(form[key] as string) ?? ''}
      inputValue={(form[key] as string) ?? ''}
      onInputChange={(_e, v) => setField(key, (v ?? '') as Form[typeof key])}
      onChange={(_e, v) => setField(key, ((v as string) ?? '') as Form[typeof key])}
      renderInput={(params) => (
        <TextField {...params} size="small" fullWidth />
      )}
    />
  );

  const input = (key: keyof Form, type?: string) => (
    <WorkspaceInput
      type={type}
      value={form[key] as string | number | null}
      onChange={(v) => setField(key, (type === 'number' ? Number(v) : v) as Form[typeof key])}
    />
  );
  /** Sections are only editable when the record is ours to edit. */
  const editProps = (id: string) =>
    canEdit
      ? { editing: isEditing(id), onEdit: () => startEdit(id), onCancel: cancelEdit, onSave: () => saveSection(id) }
      : {};

  /*
   * Salary is stored two ways and only one of them should ever be in play.
   *
   * `salaryMin`/`salaryMax` are the structured range the public site formats, sorts and puts in
   * the Google Jobs payload. `salary` is a free-text note, which none of that can read. Read mode
   * has always preferred the range and fallen back to the note, so the two never appeared together
   * on screen -- but the EDITOR offered both at once, which is how a job ends up with a range
   * saying one thing and a note saying another, and nothing to say which is true.
   */
  const hasSalaryRange = form.salaryMin != null && Number(form.salaryMin) > 0;
  /*
   * Offer the note when it is the only thing that can carry the pay -- or when it already holds
   * something, so an existing note is never stranded somewhere the editor cannot reach it. Hiding
   * it outright would leave dead text on a record with no way to clear it.
   */
  const showSalaryNote = !hasSalaryRange || Boolean(form.salary);

  /*
   * Experience is one decision, not three.
   *
   * This section used to offer `experience`, `yearsOfExperience` and `seniorityLevel` as three
   * independent controls for one question, and nothing reconciled them. Seniority is gone --
   * nothing rendered it, no employer could set it, and it carried a second vocabulary
   * (junior|mid|senior|lead|exec in the model, INTERN..EXECUTIVE in the extraction prompt) that
   * disagreed with both itself and the four levels below. `experience` is the field every
   * consumer actually reads, so it is the one an admin picks.
   *
   * A stored value that predates the select still has to be visible. Dropping it from the option
   * list would render the control blank, which reads as "no experience set" while a filter-
   * breaking string like '3-5 years' is quietly still on the record. So a legacy value is carried
   * in as its own option, labelled, and can be left alone or replaced deliberately.
   */
  const experienceOptions = (() => {
    const current = form.experience?.trim();
    if (!current || EXPERIENCE_LEVELS.some((o) => o.value === current)) return EXPERIENCE_LEVELS;
    return [...EXPERIENCE_LEVELS, { value: current, label: `${current} (legacy value)` }];
  })();

  /*
   * Advisory only, and only when the two fields actually disagree. `Management-Level` is scope
   * rather than tenure, so it is never flagged: an eight-year head of retail and an eight-year
   * individual contributor carry the same number and belong in different bands.
   */
  const experienceHint = (() => {
    const suggested = experienceLevelForYears(form.yearsOfExperience);
    if (!suggested) return undefined;
    if (!form.experience) return `${form.yearsOfExperience} years usually reads as ${suggested}.`;
    if (form.experience === suggested || form.experience === 'Management-Level') return undefined;
    return `${form.yearsOfExperience} years usually reads as ${suggested}. Leave it if this role is an exception.`;
  })();

  const salary =
    form.salaryMin != null && form.salaryMax != null && Number(form.salaryMin) > 0
      ? `${form.salaryCurrency || ''} ${form.salaryMin} to ${form.salaryMax}`.trim()
      : form.salary || 'Confidential';

  const glance: GlanceRow[] = [
    { key: 'Company', value: form.companyName || '-' },
    { key: 'Openings', value: String(form.numberOfPositions ?? 1), mono: true },
    { key: 'Type', value: form.jobType ? jobTypeLabel(form.jobType) : '-' },
  ];

  /*
   * Managed ownership, at a glance.
   *
   * The mode now shows on every job, not only on MANAGED ones. It was hidden on self-serve jobs
   * because "Unassigned" implied an action nobody could take — true while the mode was read-only,
   * and the reason a job's mode was invisible in the one place an admin looks before changing it.
   * The Managed hiring card below can now set it, so the state is worth stating either way.
   *
   * Recruiter still appears only on MANAGED roles: a self-serve job has nobody to name.
   */
  const assignedName = form.assignedHrId
    ? (hrUsers.find((u) => u._id === form.assignedHrId)?.name ?? form.assignedHrId)
    : null;
  glance.push({
    key: 'Hiring',
    value: form.hiringMode === 'MANAGED' ? 'Premium Hiring' : 'Self-serve',
  });
  if (form.hiringMode === 'MANAGED') {
    glance.push({
      key: 'Recruiter',
      value: assignedName ?? 'Unassigned',
      valueColor: assignedName ? undefined : 'var(--amber)',
    });
  }

  const chipEditor = (
    key: 'skillsRequired' | 'tags',
    value: string,
    setValue: (v: string) => void,
    sectionId: string,
    placeholder: string,
  ) => (
    <Box sx={{ gridColumn: '1 / -1' }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: isEditing(sectionId) ? 1 : 0 }}>
        {form[key].length === 0 && <span className="cc-field__value">-</span>}
        {form[key].map((item) => (
          <Chip
            key={item}
            label={item}
            size="small"
            sx={{ backgroundColor: 'var(--ink-750)', color: 'var(--tx-mid)', fontSize: 'var(--font-size-3xs)', height: 22 }}
            onDelete={isEditing(sectionId) ? () => setField(key, form[key].filter((s) => s !== item)) : undefined}
          />
        ))}
      </Box>
      {isEditing(sectionId) && (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <WorkspaceInput
            placeholder={placeholder}
            value={value}
            onChange={setValue}
            onKeyDown={(e) => {
              if (e.key !== 'Enter') return;
              e.preventDefault();
              const v = value.trim();
              if (v && !form[key].includes(v)) setField(key, [...form[key], v]);
              setValue('');
            }}
          />
          <Button
            size="small"
            onClick={() => {
              const v = value.trim();
              if (v && !form[key].includes(v)) setField(key, [...form[key], v]);
              setValue('');
            }}
          >
            Add
          </Button>
        </Box>
      )}
    </Box>
  );

  const richField = (
    label: string,
    key: 'description' | 'requirements' | 'responsibilities',
    sectionId: string,
  ) =>
    isEditing(sectionId) ? (
      <Box sx={{ gridColumn: '1 / -1' }}>
        <WorkspaceFieldLabel>{label}</WorkspaceFieldLabel>
        <TextEditor value={form[key]} setValue={(v: string) => setField(key, v)} />
      </Box>
    ) : (
      <WorkspaceField
        label={label}
        full
        value={form[key] ? <span className="cc-rich" dangerouslySetInnerHTML={{ __html: sanitizeHtml(String(form[key])) }} /> : '-'}
      />
    );

  const addApplicant = async (jobSeekerId: string) => {
    setSubmittingApplicant(true);
    try {
      if (addMode === 'sourcing') {
        // Sourcing on a managed job; a self-serve job has no recruiter stages, so the backend
        // starts it at Interested instead.
        const created = await fetchGraphQLApiData(
          createJobApplicationMutation(jobSeekerId, jobId || '', currentUserDetails()?.email ?? ''),
        );
        if (!created?.data?.createJobApplication?._id) throw new Error('The candidate was not added.');
        showToast('Candidate added to the pipeline', 'success');
      } else {
        // Right to Represent: created at Qualified and the candidate is asked. The employer
        // cannot see it in that state, so nothing reaches the client until they answer.
        const created = await fetchGraphQLApiData(
          createJobApplicationMutation(
            jobSeekerId,
            jobId || '',
            currentUserDetails()?.email ?? '',
            'Qualified',
          ),
        );
        const applicationId = created?.data?.createJobApplication?._id;
        if (!applicationId) {
          throw new Error('The application was not created, so no consent request was sent.');
        }
        await fetchGraphQLApiData(requestRightToRepresentMutation(applicationId));
        showToast('Consent requested from the candidate', 'success');
      }
      setAddApplicantOpen(false);
      setPipelineKey((k) => k + 1);
    } catch (e: any) {
      // The server refuses a duplicate by name, and a locked job with its own sentence.
      const msg = String(e?.message ?? '');
      showToast(
        msg.includes('already been submitted')
          ? 'That candidate is already on this job'
          : /not taking new candidates/.test(msg)
            ? msg.replace(/^GraphQL errors:\s*/, '')
            : 'Failed to add the candidate',
        'error',
      );
    } finally {
      setSubmittingApplicant(false);
    }
  };

  /** Accept the Filled suggestion. Suggested by the Pipeline tab, never automatic. */
  const markFilled = async () => {
    try {
      await fetchGraphQLApiData(
        updateJobDataById({ id: form?.id || jobId || '', requirementStatus: 'Filled', requirementReason: 'All openings filled' }),
        { showStatus: true, message: 'Job marked as Filled' },
      );
      if (jobId) await load(jobId);
    } catch {
      showToast('Could not mark the job as Filled', 'error');
    }
  };

  const identityRail = (
    <IdentityRail
      initials={initialsOf(form.title)}
      name={form.title || 'Job'}
      subId={form.jobCode || form.id || jobId || ''}
      statusChip={<CRMStatusChip status={form.requirementStatus} />}
      /* Mirrors "Submit to a job" on the candidate record, so the two directions look alike. The
         job side previously had no manual route in at all: the only Submit lived inside the AI
         matching tab, which needs a paid scoring run first, so an HR who already knew who they
         wanted had to go and find the candidate's own record. */
      secondaryActions={
        <Button
          size="small"
          variant="outlined"
          startIcon={<PersonAddAltOutlinedIcon fontSize="small" />}
          onClick={() => { setAddMode('consent'); setAddApplicantOpen(true); }}
        >
          Add applicant
        </Button>
      }
      glance={glance}
      audit={{
        createdAt: job.createdAt,
        createdBy: job.createdBy,
        updatedAt: job.updatedAt,
        updatedBy: job.updatedBy,
      }}
    />
  );

  const details = (
    <>
      {!canEdit && (
        <SectionCard title="External job" icon={<WorkOutlineIcon />}>
          <WorkspaceField
            label="Source"
            full
            value={`Mirrored from ${form.jobSource || 'an external source'} and read-only here — editing it locally would drift from the source.`}
          />
          <WorkspaceField label="Job URL" full mono value={form.jobUrl || '-'} />
        </SectionCard>
      )}

      <SectionCard title="Role definition" icon={<WorkOutlineIcon />} meta="8 fields" {...editProps('role')}>
        <WorkspaceField label="Title" value={form.title} editing={isEditing('role')} edit={input('title')} />
        <WorkspaceField label="Company" value={form.companyName} editing={isEditing('role')} edit={input('companyName')} />
        <WorkspaceField
          label="Requirement status"
          value={<CRMStatusChip status={form.requirementStatus} />}
          editing={isEditing('role')}
          edit={(
            <SelectFormField
              required={false}
              label=""
              name="requirementStatus"
              value={form.requirementStatus}
              options={REQUIREMENT_STATUSES.map((r) => ({ value: r, label: r }))}
              onChange={(_n, v) => setField('requirementStatus', String(v))}
              helperText={requirementHint(requirementOf(job?.requirementStatus, job?.jobStatus), form.requirementStatus)}
            />
          )}
        />
        {isEditing('role') && form.requirementStatus !== requirementOf(job?.requirementStatus, job?.jobStatus) && (
          <WorkspaceField
            label="Reason for the change"
            full
            editing
            edit={(
              <WorkspaceInput
                value={form.requirementReason}
                onChange={(v) => setField('requirementReason', v)}
                placeholder="Kept on the job's history. Required to reopen a filled or closed job."
              />
            )}
          />
        )}
        <WorkspaceField
          label="Employment type"
          value={form.jobType ? jobTypeLabel(form.jobType) : '-'}
          editing={isEditing('role')}
          edit={<SelectFormField required={false} label="" name="jobType" value={form.jobType} options={JOB_TYPES} onChange={(_n, v) => setField('jobType', String(v))} />}
        />
        <WorkspaceField
          label="Workplace"
          value={form.isRemote ? 'Remote' : 'On-site'}
          editing={isEditing('role')}
          edit={<FormControlLabel control={<Switch checked={form.isRemote} onChange={(e) => setField('isRemote', e.target.checked)} />} label="Remote" />}
        />
        <WorkspaceField label="Openings" value={String(form.numberOfPositions ?? 1)} mono editing={isEditing('role')} edit={input('numberOfPositions', 'number')} />
        <WorkspaceField label="Category" value={form.category} editing={isEditing('role')} edit={suggestInput('category', categoryOptions)} />
        <WorkspaceField label="Industry" value={form.industry} editing={isEditing('role')} edit={suggestInput('industry', industryOptions)} />
      </SectionCard>

      {/*
        * Managed hiring. Super Admins and HR Leads only -- see canManageHiring.
        *
        * This is the only place in the Control Center that can make an existing job MANAGED.
        * assignHr flips a whole client's book at once and matches jobs by employerId, which 35 of
        * Emerald Jewel's 38 listings do not have; createJob only marks jobs posted after the
        * client became premium. So every job already on the platform had no route into the
        * Managed pipeline at all, and /hr correctly showed an empty list for every client.
        */}
      {canManageHiring && (
        <SectionCard
          title="Premium Hiring"
          icon={<SupervisorAccountOutlinedIcon />}
          meta="2 fields"
          editing={isEditing('managed')}
          onEdit={() => startEdit('managed')}
          onCancel={cancelEdit}
          onSave={saveManagedHiring}
        >
          <WorkspaceField
            label="Hiring mode"
            value={
              form.hiringMode === 'MANAGED' ? (
                <CRMStatusChip status="Managed" />
              ) : (
                'Self-serve'
              )
            }
            editing={isEditing('managed')}
            edit={
              <SelectFormField
                required={false}
                label=""
                name="hiringMode"
                value={form.hiringMode || 'SELF_SERVE'}
                options={HIRING_MODE_OPTIONS}
                onChange={(_n, v) => setField('hiringMode', String(v))}
              />
            }
          />
          <WorkspaceField
            label="Recruiter"
            value={
              form.hiringMode !== 'MANAGED'
                ? '-'
                : form.assignedHrId
                  ? (hrUsers.find((u) => u._id === form.assignedHrId)?.name ?? form.assignedHrId)
                  : 'Unassigned'
            }
            editing={isEditing('managed')}
            edit={
              <SelectFormField
                required={false}
                label=""
                name="assignedHrId"
                value={form.assignedHrId ?? ''}
                options={[
                  { value: '', label: 'Unassigned' },
                  ...hrUsers.map((u) => ({ value: u._id, label: u.name })),
                ]}
                onChange={(_n, v) => setField('assignedHrId', String(v) || null)}
              />
            }
          />
          {/*
            * Said on the card rather than left to be discovered. A MANAGED job with no recruiter
            * is not a neutral half-finished state: the access rule is `hiringMode != MANAGED OR
            * assignedHrId == me`, so it hides the job's candidates from every HR at once and
            * leaves them visible only to Super Admins. That reads as "the applications
            * disappeared", and nothing on screen would connect it to this card.
            */}
          {form.hiringMode === 'MANAGED' && !form.assignedHrId && (
            <Box sx={{ gridColumn: '1 / -1' }}>
              <Typography className="cc-jobws__warn">
                {MANAGED_WITHOUT_RECRUITER_WARNING}
              </Typography>
            </Box>
          )}
        </SectionCard>
      )}

      {/*
        * Job code, source and URL describe a job that came from somewhere else, so they only
        * appear on an external one. Measured before hiding them: all four fields in this card
        * were empty on all 5,328 jobs on production, because there are zero external jobs. The
        * ingest pipeline exists and has never run, so the fields are kept rather than deleted and
        * simply do not show on a job we host ourselves.
        *
        * Expiry is the exception and moves out below. It is not about provenance at all, it is
        * used on ordinary jobs, and it decides whether a listing still accepts applications.
        */}
      {form.isExternalJob && (
        <SectionCard title="Identifiers & source" icon={<ArticleOutlinedIcon />} meta="3 fields" {...editProps('ident')}>
          <WorkspaceField label="Job code" value={form.jobCode} mono editing={isEditing('ident')} edit={input('jobCode')} />
          <WorkspaceField label="Job source" value={form.jobSource} editing={isEditing('ident')} edit={input('jobSource')} />
          <WorkspaceField label="Job URL" full mono value={form.jobUrl} editing={isEditing('ident')} edit={input('jobUrl')} />
        </SectionCard>
      )}

      <SectionCard title="Availability" icon={<ArticleOutlinedIcon />} meta="1 field" {...editProps('ident')}>
        {/* A job with no expiry date is open, not expired. Blank here is a real and common
            answer, not a missing one. */}
        <WorkspaceField label="Expiry date" value={form.expiryDate || '-'} mono editing={isEditing('ident')} edit={input('expiryDate', 'date')} />
      </SectionCard>

      <SectionCard title="Location" icon={<PlaceOutlinedIcon />} meta="5 fields" {...editProps('loc')}>
        <WorkspaceField label="Location" full value={form.location} editing={isEditing('loc')} edit={input('location')} />
        <WorkspaceField label="City" value={form.city} editing={isEditing('loc')} edit={input('city')} />
        <WorkspaceField label="State" value={form.state} editing={isEditing('loc')} edit={input('state')} />
        <WorkspaceField label="Country" value={form.country} editing={isEditing('loc')} edit={input('country')} />
        <WorkspaceField label="ZIP code" value={form.zipCode} mono editing={isEditing('loc')} edit={input('zipCode')} />
      </SectionCard>

      <SectionCard title="Compensation & experience" icon={<PaymentsOutlinedIcon />} meta={isEditing('comp') ? `${showSalaryNote ? 8 : 7} fields` : '5 fields'} {...editProps('comp')}>
        {isEditing('comp') ? (
          <>
            {/* Capped: a three-option currency select ran the full width of the card. */}
            <Box sx={{ gridColumn: '1 / -1' }} className="cc-field-row">
              <Box className="cc-field-row__grow">
                <SelectFormField required={false} label="Currency" name="salaryCurrency" value={form.salaryCurrency} options={SALARY_CURRENCIES} onChange={(_n, v) => setField('salaryCurrency', String(v))} />
              </Box>
            </Box>
            <WorkspaceField label="Min salary" value="" editing edit={input('salaryMin', 'number')} />
            <WorkspaceField label="Max salary" value="" editing edit={input('salaryMax', 'number')} />
            <WorkspaceField
              label="Pay frequency"
              value=""
              editing
              edit={
                /*
                 * A dropdown, not a text box. This was free text while the create form two
                 * screens away offered a constrained list, so the same field arrived as
                 * MONTHLY from one and 'monthly', 'Per month' or '' from the other. Nothing
                 * downstream validates it -- salaryFrequency is a plain String on the backend
                 * -- and every display map on the public site falls through to "per year" for
                 * anything it does not recognise, so a weekly job silently advertised an
                 * annual salary.
                 */
                <SelectFormField
                  required={false}
                  label=""
                  name="salaryFrequency"
                  value={form.salaryFrequency}
                  options={SALARY_FREQUENCY_OPTIONS}
                  onChange={(_n, v) => setField('salaryFrequency', String(v))}
                />
              }
            />
            {showSalaryNote && (
              <WorkspaceField
                label={hasSalaryRange ? 'Salary note (unused while a range is set)' : 'Salary note'}
                full
                value=""
                editing
                edit={input('salary')}
              />
            )}
          </>
        ) : (
          <>
            <WorkspaceField label="Salary range" value={salary} mono />
            <WorkspaceField label="Pay frequency" value={salaryFrequencyLabel(form.salaryFrequency)} />
          </>
        )}
        <WorkspaceField
          label="Experience level"
          value={form.experience}
          editing={isEditing('comp')}
          edit={
            <SelectFormField
              required={false}
              label=""
              name="experience"
              value={form.experience}
              options={experienceOptions}
              helperText={experienceHint}
              onChange={(_n, v) => setField('experience', String(v))}
            />
          }
        />
        <WorkspaceField
          label="Years required (optional)"
          value={form.yearsOfExperience ? `${form.yearsOfExperience} yrs` : ''}
          mono
          editing={isEditing('comp')}
          edit={input('yearsOfExperience', 'number')}
        />
        <WorkspaceField label="Education" value={form.education} editing={isEditing('comp')} edit={input('education')} />
      </SectionCard>

      <SectionCard title="Description" icon={<ArticleOutlinedIcon />} {...editProps('desc')}>
        {richField('Description', 'description', 'desc')}
      </SectionCard>

      <SectionCard title="Requirements" icon={<ArticleOutlinedIcon />} {...editProps('reqs')}>
        {richField('Requirements', 'requirements', 'reqs')}
      </SectionCard>

      <SectionCard title="Responsibilities" icon={<ArticleOutlinedIcon />} {...editProps('resp')}>
        {richField('Responsibilities', 'responsibilities', 'resp')}
      </SectionCard>

      <SectionCard title="Skills & tags" icon={<SellOutlinedIcon />} meta={`${form.skillsRequired.length} skills · ${form.tags.length} tags`} {...editProps('skills')}>
        <Box sx={{ gridColumn: '1 / -1' }}><WorkspaceFieldLabel>Required skills</WorkspaceFieldLabel></Box>
        {chipEditor('skillsRequired', skillInput, setSkillInput, 'skills', 'Add a skill and press Enter')}
        <Box sx={{ gridColumn: '1 / -1', mt: 1 }}><WorkspaceFieldLabel>Tags</WorkspaceFieldLabel></Box>
        {chipEditor('tags', tagInput, setTagInput, 'skills', 'Add a tag and press Enter')}
      </SectionCard>

      {/*
        * Internal only. None of this appears on the public posting.
        *
        * The account owner and the internal brief belong to a MANAGED role, where a Mintly
        * recruiter works the job for a client. On a self-serve job an employer posted themselves
        * there is no account owner and no brief, and the data agrees: both fields were empty on
        * all 5,328 jobs on production. Contact email and phone stay on every job, because 4,136
        * of them use those.
        */}
      <SectionCard title="Recruiting ops" icon={<TuneOutlinedIcon />} meta="Internal only" {...editProps('ops')}>
        {isManagedJob && (
          <WorkspaceField label="Sales / BD owner" value={form.salesOwnerName} editing={isEditing('ops')} edit={input('salesOwnerName')} />
        )}
        <WorkspaceField label="Contact email" value={form.emailAddress} mono editing={isEditing('ops')} edit={input('emailAddress')} />
        <WorkspaceField label="Contact phone" value={form.phoneNumber} mono editing={isEditing('ops')} edit={input('phoneNumber')} />
        {isManagedJob && (
        <WorkspaceField
          label="Internal filters"
          full
          value={
            isEditing('ops') ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                {form.internalFilters.map((f, i) => (
                  <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <WorkspaceInput
                      flex
                      placeholder="Field (e.g. Religion)"
                      value={f.key}
                      onChange={(v) =>
                        setField(
                          'internalFilters',
                          form.internalFilters.map((x, idx) => (idx === i ? { ...x, key: v } : x)),
                        )
                      }
                    />
                    <WorkspaceInput
                      flex
                      placeholder="Value (e.g. Local to Mumbai)"
                      value={f.value}
                      onChange={(v) =>
                        setField(
                          'internalFilters',
                          form.internalFilters.map((x, idx) => (idx === i ? { ...x, value: v } : x)),
                        )
                      }
                    />
                    <IconButton
                      size="small"
                      onClick={() =>
                        setField(
                          'internalFilters',
                          form.internalFilters.filter((_, idx) => idx !== i),
                        )
                      }
                      sx={{ color: 'var(--red)' }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
                <Button
                  size="small"
                  onClick={() =>
                    setField('internalFilters', [
                      ...form.internalFilters,
                      { key: '', value: '' },
                    ])
                  }
                  sx={{ alignSelf: 'flex-start' }}
                >
                  + Add filter
                </Button>
              </Box>
            ) : form.internalFilters.length === 0 ? (
              '-'
            ) : (
              form.internalFilters.map((f) => `${f.key}: ${f.value}`).join(' · ')
            )
          }
        />
        )}
        <WorkspaceField
          label="Data available"
          value={
            isEditing('ops') ? (
              <Switch
                checked={!!form.dataAvailable}
                onChange={(e) => setField('dataAvailable', e.target.checked)}
              />
            ) : form.dataAvailable == null ? (
              '-'
            ) : form.dataAvailable ? (
              'Yes'
            ) : (
              'No'
            )
          }
        />
      </SectionCard>

      <SectionCard title="Visibility" icon={<WorkOutlineIcon />} meta="3 fields" {...editProps('vis')}>
        {isEditing('vis') ? (
          <>
            <FormControlLabel control={<Switch checked={form.isFeatured} onChange={(e) => setField('isFeatured', e.target.checked)} />} label="Featured" />
            <FormControlLabel control={<Switch checked={form.isVerified} onChange={(e) => setField('isVerified', e.target.checked)} />} label="Verified" />
            <FormControlLabel control={<Switch checked={form.seoPosting} onChange={(e) => setField('seoPosting', e.target.checked)} />} label="SEO posting" />
          </>
        ) : (
          <>
            <WorkspaceField label="Featured" value={form.isFeatured ? 'Yes' : 'No'} />
            <WorkspaceField label="Verified" value={form.isVerified ? 'Yes' : 'No'} />
            <WorkspaceField label="SEO posting" value={form.seoPosting ? 'Yes' : 'No'} />
          </>
        )}
      </SectionCard>

      {canWriteJobs && (
      <SectionCard title="Danger zone" icon={<DeleteOutlineIcon />}>
        <Box sx={{ gridColumn: '1 / -1' }}>
          <Button variant="outlined" color="error" size="small" onClick={() => setConfirmDelete(true)}>
            Delete job
          </Button>
          <Typography variant="body2" sx={{ color: 'var(--tx-low)', mt: 1 }}>
            Permanent, and applications already submitted are left orphaned. Closing the job keeps
            both the record and its applications.
          </Typography>
        </Box>
      </SectionCard>
      )}
    </>
  );

  const candidates = form.isExternalJob ? (
    <SectionCard title="Candidates" icon={<AutoAwesomeOutlinedIcon />}>
      <WorkspaceField
        label="Matching"
        full
        value="External jobs are not scored or matched here — candidates apply at the source."
      />
    </SectionCard>
  ) : (
    <>
      <SectionCard title="AI matching" icon={<AutoAwesomeOutlinedIcon />}>
        <Box sx={{ gridColumn: '1 / -1' }}>
          <Box sx={{ mb: 1.5 }}><MintlyAIBadge /></Box>
          {/* CRMTabs, not a per-page <Tabs> with its own sx. `--embedded` because this bar sits
              inside a workspace section rather than at the top of a page: without it the shared
              bar brings a page-top bar's inset, fill and bottom rule into a card that already
              has all three. */}
          <CRMTabs
            sticky={false}
            className="crm-tab-bar--embedded"
            value={aiTab}
            onChange={setAiTab}
            tabs={[{ label: 'Relevant candidates' }, { label: 'AI suggested' }]}
          />

          {aiTab === 0 &&
            (relevantLoading ? (
              <Loader />
            ) : (
              <RelevancyCandidatesTable
                candidates={relevant.slice(relevantPage * PAGE, (relevantPage + 1) * PAGE)}
                page={relevantPage}
                rowsPerPage={PAGE}
                total={relevant.length}
                onPageChange={(_e, p) => setRelevantPage(p as number)}
              />
            ))}

          {aiTab === 1 && <SuggestedCandidatesTab jobId={form.id} />}
        </Box>
      </SectionCard>

      <SectionCard title="Nearby candidates" icon={<PlaceOutlinedIcon />} meta={`${nearby.length} in range`}>
        <Box sx={{ gridColumn: '1 / -1' }}>
          {nearbyLoading ? (
            <Loader />
          ) : (
            <RelevancyCandidatesTable
              candidates={nearby.slice(nearbyPage * PAGE, (nearbyPage + 1) * PAGE)}
              page={nearbyPage}
              rowsPerPage={PAGE}
              total={nearby.length}
              showAiMatch={false}
              onPageChange={(_e, p) => setNearbyPage(p as number)}
              emptyMessage="No nearby candidates for this job."
            />
          )}
        </Box>
      </SectionCard>
    </>
  );

  const tabContent: Record<string, React.ReactNode> = {
    details,
    candidates,
    pipeline: (
      <PipelineTab
        key={pipelineKey}
        jobId={jobId || ''}
        managed={form.hiringMode === 'MANAGED'}
        numberOfPositions={form.numberOfPositions}
        requirementStatus={requirementOf(job?.requirementStatus, job?.jobStatus)}
        hrUsers={hrUsers}
        listLink={jobApplicantsPath(jobId || '', [form.title, form.companyName].filter(Boolean).join(' · '))}
        onAddCandidate={() => { setAddMode('sourcing'); setAddApplicantOpen(true); }}
        onMarkFilled={markFilled}
      />
    ),
    seo: (
      <SectionCard title="SEO metadata" icon={<SearchOutlinedIcon />} meta="4 fields" {...editProps('seo')}>
        <WorkspaceField label="Meta title" full value={form.metaTitle} editing={isEditing('seo')} edit={input('metaTitle')} />
        <WorkspaceField label="Canonical URL" full mono value={form.metaUrl} editing={isEditing('seo')} edit={input('metaUrl')} />
        <WorkspaceField label="Meta description" full value={form.metaDescription} editing={isEditing('seo')} edit={input('metaDescription')} />
        {isEditing('seo') && (
          <Button size="small" disabled={generatingSeo} onClick={generateSeoDescription}>
            {generatingSeo ? 'Generating...' : 'Generate with AI'}
          </Button>
        )}
        <WorkspaceField label="Keywords" full value={form.metaKeywords} editing={isEditing('seo')} edit={input('metaKeywords')} />
      </SectionCard>
    ),
    history: (
      <>
        <SectionCard title="Record activity" icon={<HistoryIcon />}>
          <WorkspaceField label="Created" value={`${fmt(job.createdAt)} · ${job.createdBy || 'System'}`} full />
          <WorkspaceField label="Last updated" value={`${fmt(job.updatedAt)} · ${job.updatedBy || 'System'}`} full />
        </SectionCard>
        <RecordActivityCard recordId={jobId} />
      </>
    ),
  };

  return (
    <>
    <EntityWorkspace
      backLabel="Jobs"
      onBack={() => navigate('/jobs')}
      identityRail={identityRail}
      tabs={TABS}
      activeTabId={activeTab}
      onTabChange={setActiveTab}
      activityRail={<ActivityRail timeline={timeline} />}
      activityOpen={activityOpen}
      onToggleActivity={toggleActivity}
      isDirty={dirty && !!editing}
      dirtySectionLabel={editing ?? ''}
      onDiscard={cancelEdit}
      onSave={() => { if (editing) saveSection(editing); }}
    >
      {tabContent[activeTab]}
      <ConfirmDialog
        open={confirmDelete}
        title="Delete this job permanently?"
        body={
          <>
            Applications already submitted to <strong>{form?.title || 'this job'}</strong> are not
            deleted and will be orphaned. Setting the status to Closed keeps the record and its
            applications intact.
          </>
        }
        onConfirm={handleDeleteJob}
        onCancel={() => setConfirmDelete(false)}
      />
    </EntityWorkspace>

    <RecordPickerDialog
      open={addApplicantOpen}
      title={addMode === 'sourcing' ? 'Add a candidate to the pipeline' : 'Add an applicant to this job'}
      searchPlaceholder="Search candidates by name or email"
      confirmLabel={addMode === 'sourcing' ? 'Add candidate' : 'Add applicant and ask for consent'}
      submitting={submittingApplicant}
      onSearch={searchCandidates}
      onConfirm={addApplicant}
      onClose={() => setAddApplicantOpen(false)}
    />
    </>
  );
};

export default JobWorkspace;
